import React from 'react'
import { Link } from 'react-router-dom'

/**
 * Terms of Service — public, anonymous-reachable. Cross-links to the Privacy
 * Policy. Rendered with the app's design tokens (no raw hex).
 */

const sectionStyle: React.CSSProperties = { marginBottom: 28 }
const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 10px', letterSpacing: '-0.01em' }
const pStyle: React.CSSProperties = { fontSize: 15, color: 'var(--ink-secondary)', lineHeight: 1.7, margin: 0 }
const ulStyle: React.CSSProperties = { fontSize: 15, color: 'var(--ink-secondary)', lineHeight: 1.7, margin: '8px 0 0', paddingLeft: 20 }
const liStyle: React.CSSProperties = { marginBottom: 4 }
const highlightStyle: React.CSSProperties = { padding: '16px 18px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 12, margin: '0 0 12px' }
const highlightLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-text)', margin: '0 0 6px' }
const linkStyle: React.CSSProperties = { color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 }

export default function Terms() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', color: 'var(--ink-primary)', padding: '48px 24px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Link to="/" style={{ fontSize: 13, color: 'var(--ink-tertiary)', textDecoration: 'none' }}>← Back to home</Link>

        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.03em', margin: '20px 0 8px' }}>Terms of Service</h1>
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 24px' }}>Last updated: July 8, 2026</p>

        <p style={{ ...pStyle, marginBottom: 24 }}>
          These Terms of Service govern your use of the Orkestra platform. By using Orkestra, you agree to
          these Terms.
        </p>

        <div style={sectionStyle}>
          <h2 style={h2Style}>1. The Service</h2>
          <p style={pStyle}>Orkestra provides software for service businesses to manage jobs, scheduling, invoicing, quotes, and customer communications, including email and SMS. We may update the Service over time.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>2. Accounts and Eligibility</h2>
          <p style={pStyle}>You agree to provide accurate information, to safeguard your credentials, and that you are responsible for activity on your account. You must be at least 18 years old and able to enter into a contract.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>3. Acceptable Use</h2>
          <p style={pStyle}>You agree not to:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Use the Service for unlawful, harmful, or deceptive purposes.</li>
            <li style={liStyle}>Message recipients without proper consent or in violation of law or carrier rules.</li>
            <li style={liStyle}>Transmit prohibited content.</li>
            <li style={liStyle}>Interfere with, reverse-engineer, or gain unauthorized access to the Service.</li>
            <li style={liStyle}>Infringe the rights of others.</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>4. Customer Communications and Messaging Compliance</h2>
          <div style={highlightStyle}>
            <p style={highlightLabel}>Your responsibility</p>
            <p style={{ ...pStyle, color: 'var(--ink-primary)' }}>
              You are solely responsible for obtaining and maintaining valid consent from your own customers
              before sending them SMS or email through the Service, and for complying with all applicable laws
              and regulations governing those communications (including, in the United States, the TCPA and
              CTIA guidelines and carrier A2P/10DLC requirements). You must honor opt-out requests (such as
              STOP) promptly.
            </p>
          </div>
          <p style={pStyle}>Orkestra provides consent-capture and opt-out tools, but the business remains responsible for lawful use of the Service.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>5. Fees and Payment</h2>
          <p style={pStyle}>The Service is offered on a subscription basis, billed through a third-party payment processor. You authorize the applicable charges. Fees are non-refundable except as required by law, and may change on notice.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>6. Your Data</h2>
          <p style={pStyle}>
            You own Your Content. You grant us a limited license to host, process, and transmit it as needed to
            provide the Service. Our handling of personal information is described in our{' '}
            <Link to="/privacy" style={linkStyle}>Privacy Policy</Link>.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>7. Third-Party Services</h2>
          <p style={pStyle}>The Service relies on third-party providers for hosting, payments, email, and SMS. Their terms may apply, and we are not responsible for third-party services outside our control.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>8. Intellectual Property</h2>
          <p style={pStyle}>Orkestra owns the Service (excluding Your Content). You receive a limited right to use it.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>9. Termination</h2>
          <p style={pStyle}>You may stop using the Service at any time. We may suspend or terminate access for violations or to protect the Service. Some provisions survive termination.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>10. Disclaimers</h2>
          <p style={pStyle}>THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>11. Limitation of Liability</h2>
          <p style={pStyle}>To the maximum extent permitted by law, Orkestra will not be liable for any indirect, incidental, special, consequential, or punitive damages. Our total liability is capped at the amounts you paid in the twelve months preceding the claim.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>12. Indemnification</h2>
          <p style={pStyle}>You agree to indemnify Orkestra against claims arising from your use of the Service, Your Content, your messaging practices, or your violations of these Terms.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>13. Changes to These Terms</h2>
          <p style={pStyle}>We may update these Terms from time to time. When we do, we will revise the “Last updated” date above. Continued use of the Service constitutes acceptance of the updated Terms.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>14. Governing Law</h2>
          <p style={pStyle}>These Terms are governed by the laws of the State of Texas, USA.</p>
        </div>

        <div style={sectionStyle}>
          <h2 style={h2Style}>15. Contact</h2>
          <p style={pStyle}>
            Orkestra — questions about these Terms can be sent to{' '}
            <a href="mailto:support@orkestrapro.com" style={linkStyle}>support@orkestrapro.com</a>.
          </p>
        </div>
      </div>
    </div>
  )
}
